import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Goal, Milestone, DailyTask } from '../../types/goals';
import MainTabContent from '../ui/MainTabContent';
import MilestonesTabContent from '../ui/MilestonesTabContent';
import TasksTabContent from '../ui/TasksTabContent';

interface GoalDetailsProps {
  goal: Goal;
  milestones: Milestone[];
  dailyTasks: DailyTask[];
  onClose: () => void;
  toggleTaskCompletion: (taskId: string) => void;
}

export function GoalDetails({
  goal,
  milestones,
  dailyTasks,
  onClose,
  toggleTaskCompletion,
}: GoalDetailsProps) {
  const [activeTab, setActiveTab] = useState("main");
  const [selectedMilestone, setSelectedMilestone] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<string>("");

  const totalTasks = dailyTasks.length;
  const completedTasks = dailyTasks.filter(task => task.isCompleted).length;
  const progress = Math.round((completedTasks / totalTasks) * 100) || 0;

  const currentMilestone = [...milestones]
    .sort((a, b) => a.order - b.order)
    .find(milestone => !milestone.isCompleted);

  const getMilestoneProgress = (milestoneId: string) => {
    const milestoneTasks = dailyTasks.filter(task => task.milestoneId === milestoneId);
    const completedMilestoneTasks = milestoneTasks.filter(task => task.isCompleted);
    return Math.round((completedMilestoneTasks.length / milestoneTasks.length) * 100) || 0;
  };

  const handleMilestoneClick = (milestoneId: string) => {
    setSelectedMilestone(milestoneId);
    setActiveTab("tasks");
  };

  const handleToggleTaskCompletion = async (taskId: string) => {
    toggleTaskCompletion(taskId);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Variantes para animar el backdrop
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  // Variantes para animar el modal
  const modalVariants = {
    hidden: { x: "100%" },
    visible: { x: 0 },
    exit: { x: "100%" },
  };

  return (
    <AnimatePresence mode='wait'>
      <motion.div
        className="fixed inset-0 flex items-center justify-center p-4 z-50 bg-black/50"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.3 }}
        onClick={handleBackdropClick}
      >
        <motion.div
          layout
          transition={{ type: "spring", damping: 25, stiffness: 300, duration: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto relative"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 z-10"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="p-6">
            <Tabs
              defaultValue="main"
              className="w-full"
              value={activeTab}
              onValueChange={setActiveTab}
            >
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="main">Principal</TabsTrigger>
                <TabsTrigger value="milestones">Hitos</TabsTrigger>
                <TabsTrigger value="tasks">Tareas</TabsTrigger>
              </TabsList>

              <TabsContent value="main">
                <MainTabContent
                  goal={goal}
                  dailyTasks={dailyTasks}
                  milestones={milestones}
                  getMilestoneProgress={getMilestoneProgress}
                  updateTask={handleToggleTaskCompletion}
                  currentMilestone={currentMilestone}
                  progress={progress}
                  completedTasks={completedTasks}
                />
              </TabsContent>

              <TabsContent value="milestones">
                <MilestonesTabContent
                  milestones={milestones}
                  currentMilestone={currentMilestone}
                  getMilestoneProgress={getMilestoneProgress}
                  onMilestoneClick={handleMilestoneClick}
                />
              </TabsContent>

              <TabsContent value="tasks">
                <TasksTabContent
                  dailyTasks={dailyTasks}
                  milestones={milestones}
                  selectedMilestone={selectedMilestone}
                  setSelectedMilestone={setSelectedMilestone}
                  dateFilter={dateFilter}
                  setDateFilter={setDateFilter}
                />
              </TabsContent>
            </Tabs>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
